import { useMemo } from 'react';
import {
  AssessmentType,
  BasketballAssessment,
  BasketballAssessmentDetails,
  ExerciseSide,
  SoccerAssessment,
  SoccerAssessmentDetails,
} from 'types/api';
import { formatDate } from 'util/formatDate';
import { labels } from 'util/graphConstants';
import { unitsOfMeasure } from 'util/unitsOfMeasure';
import { IDefinitions } from 'components/GraphTable';
import { RadarData } from 'components/Radar';
import {
  kickKneeTableGraphTerms,
  stanceKneeTableGraphTerms,
} from './SoccerTableGraphTerms';
import { ConfigData } from './useConfig';

const {
  deltaFemoralRotateL,
  hipActiveDecelL,
  maxRelRotationL,
  translationL,
  translationKick,
  relativeRotationKick,
  deltaHipFlexionKick,
  translationStance,
  relativeRotationStance,
  deltaHipFlexionStance,
} = labels;

const basketballDefinitions: IDefinitions[] = [
  {
    key: 'assessmentDetails.assessmentBasketballMechanicsSummary.deltaFemoralRotationLeft',
    unit: unitsOfMeasure('rotation'),
    name: deltaFemoralRotateL,
  },
  {
    key: 'assessmentDetails.assessmentBasketballMechanicsSummary.maxHipActiveDecelerationLeft',
    unit: unitsOfMeasure('deceleration'),
    name: hipActiveDecelL,
  },
  {
    key: 'assessmentDetails.assessmentBasketballMechanicsSummary.maxRelativeRotationLeft',
    unit: unitsOfMeasure('rotation'),
    name: maxRelRotationL,
  },
  {
    key: 'assessmentDetails.assessmentBasketballMechanicsSummary.translationLeft',
    unit: unitsOfMeasure('translation'),
    name: translationL,
  },
];

export const useLeftKneeData = (
  assessments: Partial<SoccerAssessment | BasketballAssessment>[],
): ConfigData => {
  return useMemo(() => {
    const chartData: Array<number>[] = [];
    const radarData: RadarData[] = [];
    let barLabels: Array<string> = [];
    let definitions: IDefinitions[] = [];
    let terms: string[] = [];
    const dates: string[] = assessments.map((x) =>
      formatDate(x.date).toString(),
    );

    const lineData: { name: string; type: string; data: number[] }[] = [
      {
        name: 'Translation',
        type: 'line',
        data: [],
      },
      {
        name: 'Rel Rotation',
        type: 'line',
        data: [],
      },
      {
        name: 'Hip Decel',
        type: 'line',
        data: [],
      },
    ];
    const [first] = assessments;
    switch (first?.assessmentType) {
      case AssessmentType.SOCCER:
        terms = ['relRotation', 'translation', 'deltaHipFlex'];
        assessments?.forEach((assessment) => {
          const details =
            assessment?.assessmentDetails as SoccerAssessmentDetails;

          const {
            percTranslationKick = 0,
            percRelativeRotationKick = 0,
            percDeltaHipFlexionKick = 0,
            percTranslationStance = 0,
            percRelativeRotationStance = 0,
            percDeltaHipFlexionStance = 0,
            kickLeg,
          } = details?.assessmentSoccerMechanicsSummary || {};
          let values = [];
          if (kickLeg === ExerciseSide.LEFT) {
            values = [
              percTranslationKick,
              percRelativeRotationKick,
              percDeltaHipFlexionKick,
            ];
            chartData.push(values);
            barLabels = [
              translationKick,
              relativeRotationKick,
              deltaHipFlexionKick,
            ].map((label) => label.replace('#', 'L'));

            definitions = kickKneeTableGraphTerms;
          } else {
            values = [
              percTranslationStance,
              percRelativeRotationStance,
              percDeltaHipFlexionStance,
            ];
            chartData.push(values);

            barLabels = [
              translationStance,
              relativeRotationStance,
              deltaHipFlexionStance,
            ].map((label) => label.replace('#', 'L'));

            definitions = stanceKneeTableGraphTerms;
          }
          values.forEach((value, index) => lineData[index].data.push(value));
        });
        definitions = definitions.map((term) => ({
          ...term,
          name: term?.name?.replace('#', 'L'),
        }));
        break;
      case AssessmentType.BASKETBALL:
      default:
        terms = [
          'femoralRotation',
          'hipActDecel',
          'relRotation',
          'translation',
        ];
        definitions = basketballDefinitions;
        barLabels = [
          deltaFemoralRotateL,
          hipActiveDecelL,
          maxRelRotationL,
          translationL,
        ];
        lineData.push({
          name: 'Femoral Rotation',
          type: 'line',
          data: [],
        });
        assessments?.forEach((assessment) => {
          const details =
            assessment?.assessmentDetails as BasketballAssessmentDetails;

          const {
            percDeltaFemoralRotationLeft = 0,
            percMaxHipActiveDecelerationLeft = 0,
            percMaxRelativeRotationLeft = 0,
            percTranslationLeft = 0,
          } = details?.assessmentBasketballMechanicsSummary || {};
          const values = [
            percDeltaFemoralRotationLeft,
            percMaxHipActiveDecelerationLeft,
            percMaxRelativeRotationLeft,
            percTranslationLeft,
          ];
          chartData.push(values);
          values.forEach((value, index) => lineData[index].data.push(value));
        });
    }
    return {
      chartData,
      radarData,
      barLabels,
      definitions,
      dates,
      lineData,
      terms,
    };
  }, [assessments]);
};
